// Redux/Slices/Pages/Client/ServiceListSlice.js
// Manages: services offered by a specific business (for client detail/browse page)
// BZV2 backend: GET /discovery/details/:businessId/services
// Renamed from: Services.js (Service slice)
// Naming: services → services, serviceCategories → serviceGroups

import { createSlice } from '@reduxjs/toolkit';
import { plainAxios } from '../../../../Common/Interceptors/AxiosConfig';
import { fetchCatalogHierarchy } from '../../Common/Catalog/CatalogSlice';

const initialState = {
  errors: null,
  serviceGroups: [],            // renamed from serviceCategories (taxonomy, from catalog)
  allServices: [],             // renamed from allServices
  isLoadingAll: false,
  fetchedAllAt: null,
  fetchedCoordsAt: null,

  servicesByBusinessId: {},    // renamed from servicesByServiceId
  isLoadingByBusiness: false,
  fetchedByBusinessAt: {},

  selectedServices: [],        // renamed from selectedServices
};

const serviceListSlice = createSlice({
  name: 'serviceList',         // renamed from Service
  initialState,
  reducers: {
    setErrors: (state, action) => { state.errors = action.payload; },
    setAllServices: (state, action) => { state.allServices = action.payload; },
    setLoadingAll: (state, action) => { state.isLoadingAll = action.payload; },
    setFetchedAllAt: (state, action) => { state.fetchedAllAt = action.payload; },
    setFetchedCoordsAt: (state, action) => { state.fetchedCoordsAt = action.payload; },
    setServicesByBusinessId: (state, action) => {
      const { businessId, data } = action.payload;
      state.servicesByBusinessId[businessId] = data;
      state.fetchedByBusinessAt[businessId] = Date.now();
    },
    setLoadingByBusiness: (state, action) => { state.isLoadingByBusiness = action.payload; },
    setSelectedServices: (state, action) => { state.selectedServices = action.payload; },
    clearSelectedServices: (state) => { state.selectedServices = []; },
    setServiceGroups: (state, action) => { state.serviceGroups = action.payload; },
  },
});

// ── Thunks ────────────────────────────────────────────────────────────────────

/** Load full catalog hierarchy (use CatalogSlice — avoids duplicate fetches) */
export const loadServiceGroups = () => (dispatch) => {
  dispatch(fetchCatalogHierarchy());
};

/** GET /discovery/details/:businessId/services */
export const getServicesByBusinessId = (businessId) => async (dispatch, getState) => {
  if (!businessId) { dispatch(setErrors({ message: 'Business ID is required' })); return; }

  const fetchedAt = getState().serviceList.fetchedByBusinessAt[businessId];
  const FIVE_MIN = 5 * 60 * 1000;
  if (fetchedAt && Date.now() - fetchedAt < FIVE_MIN) return;

  dispatch(setLoadingByBusiness(true));
  dispatch(setErrors(null));

  try {
    const res = await plainAxios.get(`/discovery/details/${businessId}/services`);
    const { success, data, message } = res.data || {};
    if (success) {
      dispatch(setServicesByBusinessId({ businessId, data: data || [] }));
    } else {
      dispatch(setErrors({ message: message || 'Failed to fetch services' }));
      dispatch(setServicesByBusinessId({ businessId, data: [] }));
    }
  } catch (err) {
    const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch services';
    dispatch(setErrors({ message: errorMsg }));
    dispatch(setServicesByBusinessId({ businessId, data: [] }));
  } finally {
    dispatch(setLoadingByBusiness(false));
  }
};

export const {
  setErrors, setAllServices, setLoadingAll, setFetchedAllAt,
  setFetchedCoordsAt, setServicesByBusinessId, setLoadingByBusiness,
  setSelectedServices, clearSelectedServices, setServiceGroups,
} = serviceListSlice.actions;

export default serviceListSlice.reducer;
